'use client';
import {
  SearchAnalyticsBoundary,
  SearchAnalyticsItem,
  type SearchAnalyticsData,
} from '@eliadmin/core/src/components/analytics/search-analytics-boundary';
import { SearchSort } from '@eliadmin/core/src/components/header/search-bar/search-sort';
import { searchSortOptions } from '@eliadmin/core/src/utils/search-sort';

import { buildProductsRequest } from '@eliadmin/core/src/api/products/product-list-request';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { TrackComponent } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { ProductCard } from '@eliadmin/core/src/components/product/product-card';
import { Pagination } from '@eliadmin/core/src/components/ui/pagination';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import {
  buildProductsQuery,
  parseProductSearchParams,
} from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import { usePathname, useSearchParams } from 'next/navigation';
import { Suspense, useMemo } from 'react';
import { Breadcrumbs } from '../../components/homepage/breadcrumbs';

export default function SearchPage() {
  const locale = useLocaleStore((s) => s.currentLocale);
  const { me, isInitialized } = useCustomerStore();

  const pathname = usePathname();
  const searchParams = useSearchParams();

  const parsed = useMemo(() => {
    const query = parseProductSearchParams(searchParams);
    // Mediatip uses the backend field selection, including for old bookmarked URLs.
    delete query.search_fields;
    return query;
  }, [searchParams]);

  const query = buildProductsQuery({
    ...parsed,
    results: 12,
    ...searchSortOptions(searchParams),
  });

  const productRequestUri = buildProductsRequest(query).uri;
  const productResetUri = buildProductsRequest({ ...query, page: undefined }).uri;

  const {
    data: products,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [CacheKey.PRODUCTS, locale, productRequestUri, me?.authToken ?? null],
    queryFn: () =>
      fetchProducts({
        locale,
        query,
        authToken: me?.authToken,
      }),
    enabled: isInitialized,
    refetchOnWindowFocus: false,
  });

  return (
    <>
      <section className={cn('custom-section', 'gap-6 pb-24 pt-12 sm:gap-8 lg:gap-12')}>
        <div
          className={cn(
            'custom-container',
            'flex flex-col items-center justify-center gap-4 sm:gap-5 lg:gap-6',
          )}
        >
          <Breadcrumbs
            breadcrumb={{ name: 'Vyhľadané produkty', slug: pathname }}
            className={{
              wrapper: 'items-center justify-center',
              list: 'items-center justify-center',
            }}
          />
          <h2 className={cn('h1', 'w-full text-center font-semibold tracking-tight text-black')}>
            Vyhľadané{' '}
            <span className="text-primary">&quot;{searchParams.get('search') ?? ''}&quot;</span>
          </h2>
          {!isLoading && (
            <p className="w-full text-center text-xs sm:text-sm">
              {products?.meta?.total ?? 0} produktov
            </p>
          )}
        </div>
        <div className={cn('custom-container', 'hidden items-center justify-end sm:flex')}></div>
        {!!products?.data?.length && (
          <TrackComponent
            eventName={'view_item_list'}
            eventParams={{
              item_list_id: `search:${searchParams.get('search') ?? ''}`,
              item_list_name: 'search',
            }}
            items={products.data}
          />
        )}
        <div className={cn('custom-container', 'flex flex-col items-start justify-start')}>
          <div className="flex w-full flex-col items-center gap-6 sm:gap-8 lg:gap-12">
            {isLoading ? (
              <div className={cn('flex h-[300px] w-full items-center justify-center')}>
                <Spinner />
              </div>
            ) : (
              <>
                <>
                  <SearchSort />
                  <SearchAnalyticsBoundary
                    mode="submitted"
                    active={!isFetching}
                    data={{
                      manifest: products?.meta?.search,
                      identity: JSON.stringify([
                        locale,
                        buildProductsRequest(query).uri,
                        me?.customer.id ?? null,
                      ]),
                      locale,
                      results: (products?.data ?? []).map((product) => ({
                        type: 'product',
                        id: product.id,
                        href: product.slug,
                      })),
                      offset: products?.meta?.from
                        ? products.meta.from - 1
                        : (Number(query.page ?? 1) - 1) * Number(query.results ?? 12),
                    }}
                  >
                    <div
                      className={cn(
                        'grid w-full grid-cols-2 gap-x-3 gap-y-6 md:grid-cols-3 md:gap-x-6 md:gap-y-12 xl:grid-cols-4',
                      )}
                    >
                      {products?.data.map((product, searchIndex) => (
                        <SearchAnalyticsItem index={searchIndex} key={searchIndex}>
                          {<ProductCard key={`product-${product.id}`} product={product} />}
                        </SearchAnalyticsItem>
                      ))}
                    </div>
                  </SearchAnalyticsBoundary>
                </>
              </>
            )}
            <Suspense>
              <Pagination links={products?.meta?.links ?? []} resetWhen={[productResetUri]} />
            </Suspense>
          </div>
        </div>
      </section>
    </>
  );
}
